import {
  OrganisationProductSelectionTable,
  type OrganisationProductSelectionTableFeature,
  type OrganisationSelectableProductListing,
} from "@fero/components";
import { Grid, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import type { BigDecimal } from "adl-gen/common";
import type { WithDbId } from "adl-gen/common/db";
import type {
  NominatedPurchaserDetailsView,
  ProductSalePriceInfo,
  ThirdPartySaleFinishableProductListing,
  ThirdPartySaleFinishedProductListing,
} from "adl-gen/ferovinum/app/api";
import type {
  PurchaseRequestSalePriceType,
  StorageLocation,
} from "adl-gen/ferovinum/app/db";
import { BaseWizardCard } from "components/library/widgets/wizard-card/base-wizard-card";
import type { FormikProps } from "formik";
import _ from "lodash";
import { useMemo, useState } from "react";
import { assertNever } from "utils/hx/util/types";
import type { StorageListing } from "../../purchase-request-types";
import type { SetupPurchaserRequestFormValues } from "../organisation-purchase-request-setup-page";

export const ProductSelectionCard = ({
  selectedPurchaser,
  finishableProductListings,
  finishedProductListings,
  form,
  productsDefaultSalePrices,
  activeStorageLocation,
  showProductionOrders,
}: {
  selectedPurchaser: NominatedPurchaserDetailsView;
  finishedProductListings: StorageListing[];
  finishableProductListings: StorageListing[];
  form: FormikProps<SetupPurchaserRequestFormValues>;
  productsDefaultSalePrices: ProductSalePriceInfo[];
  activeStorageLocation?: WithDbId<StorageLocation>;
  showProductionOrders?: boolean;
}) => {
  const selectableProducts: OrganisationSelectableProductListing[] =
    useMemo(() => {
      if (!activeStorageLocation) {
        return [];
      }
      const finishedProducts =
        (finishedProductListings.find(
          (p) => p.storageLocation.id === activeStorageLocation.id,
        )?.products as ThirdPartySaleFinishedProductListing[]) ?? [];
      const finishableProducts =
        (finishableProductListings.find(
          (p) => p.storageLocation.id === activeStorageLocation.id,
        )?.products as ThirdPartySaleFinishableProductListing[]) ?? [];

      const results: OrganisationSelectableProductListing[] =
        finishedProducts.map((p) => {
          const foundDefaultPriceInfo = productsDefaultSalePrices.find(
            (product) => product.productCode === p.productWithId.value.code,
          );
          return {
            productWithId: p.productWithId,
            stages: p.dealLegStages.map((leg) => ({
              kind: "deal-leg",
              value: leg,
            })),
            defaultSalePrice: extractPrice(
              foundDefaultPriceInfo,
              form.values.salePriceType,
            ),
          };
        });

      if (showProductionOrders) {
        finishableProducts.forEach((p) => {
          const existing = results.find(
            (r) => r.productWithId.id === p.productWithId.id,
          );
          if (existing) {
            existing.stages.push({ kind: "producible", value: p });
          } else {
            results.push({
              productWithId: p.productWithId,
              stages: [
                { kind: "producible", value: _.omit(p, "productWithId") },
              ],
            });
          }
        });
      }
      return results;
    }, [
      activeStorageLocation,
      finishedProductListings,
      showProductionOrders,
      finishableProductListings,
      form.values.salePriceType,
      productsDefaultSalePrices,
    ]);

  const { initFeatures, initToggleGroups } = useMemo(() => {
    const initFeatures = {
      freeStock: form.values.products?.some((p) => p.freeUnits),
      productDiscount: form.values.products?.some((p) => p.priceDiscount),
      dealDiscount: form.values.dealDiscountAmount ?? undefined,
    } as { [key in OrganisationProductSelectionTableFeature]?: boolean };
    const initToggleGroups = [
      ...(initFeatures.freeStock ? ["freeStock"] : []),
      ...(initFeatures.productDiscount ? ["productDiscount"] : []),
      ...(initFeatures.dealDiscount ? ["dealDiscount"] : []),
    ] as OrganisationProductSelectionTableFeature[];
    return { initFeatures, initToggleGroups };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [features, setFeatures] = useState(initFeatures);
  const [toggleGroups, setToggleGroups] = useState(initToggleGroups);

  return (
    <Stack spacing={5}>
      <BaseWizardCard
        title={"Stock Selection"}
        subtitle={
          activeStorageLocation
            ? "Please select the stock you would like to include in this sale."
            : "Please select a Storage Location"
        }
        inputElement={
          <Stack width={"100%"}>
            <Grid container justifyContent="flex-end">
              <ToggleButtonGroup
                value={toggleGroups}
                onChange={(
                  _,
                  newGroups: OrganisationProductSelectionTableFeature[],
                ) => {
                  setToggleGroups(newGroups);
                  setFeatures({
                    freeStock: newGroups.includes("freeStock"),
                    productDiscount: newGroups.includes("productDiscount"),
                    dealDiscount: newGroups.includes("dealDiscount"),
                  });
                }}
              >
                <ToggleButton value="productDiscount">
                  Product Discount
                </ToggleButton>
                <ToggleButton value="freeStock">Free Stock</ToggleButton>
                <ToggleButton value="dealDiscount">Deal Discount</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <OrganisationProductSelectionTable
              key={activeStorageLocation?.id ?? "no-location"}
              purchaserCurrency={selectedPurchaser.currency}
              form={form}
              productListings={selectableProducts}
              features={features}
            />
          </Stack>
        }
      />
    </Stack>
  );
};

function extractPrice(
  priceInfo: ProductSalePriceInfo | undefined,
  salePriceType: PurchaseRequestSalePriceType,
): BigDecimal | undefined {
  switch (salePriceType) {
    case "incDutyExVat":
      return priceInfo?.incDutyExVatPrice ?? undefined;
    case "incDutyAndVat":
      return priceInfo?.incDutyAndVatPrice ?? undefined;
    case "exDutyAndVat":
      return priceInfo?.exDutyAndVatPrice ?? undefined;
    default:
      assertNever(salePriceType);
  }
}
